import { DateTime } from 'luxon';
import getDisplayName from 'data/services/profile/getDisplayName';
import { INVOICE_CHANGE_REASON_MAX_LENGTH } from './constants';
import {
    Invoice,
    InvoiceAcceptance,
    InvoiceCreateStatus,
    InvoiceBuilderColumn,
    InvoiceBuilderCustomColumn,
    InvoiceBuilderTemplate,
    InvoiceBillToCustomField,
    InvoiceBillToCustomValue,
    InvoiceCustomer,
    InvoiceContentBody,
    InvoiceCustomValue,
    InvoiceFee,
    InvoiceLine,
    InvoiceRegionalSettings,
    InvoiceSignatureState,
    InvoiceStatus,
    ListInvoicesQuery,
    PaperSize,
} from './types';

export const todayIsoDate = () => DateTime.now().toISODate() || '';

export const normalizePaperSize = (paperSize?: PaperSize): PaperSize => (
    paperSize === 'Letter' ? 'Letter' : 'A4'
);

export const addDaysIso = (isoDate: string, days: number) => {
    const parsed = DateTime.fromISO(isoDate);
    if (!parsed.isValid) return todayIsoDate();
    return parsed.plus({ days }).toISODate() || todayIsoDate();
};

export const toIsoDate = (value?: Date | string | null) => {
    if (!value) return '';
    if (typeof value === 'string') {
        const parsed = DateTime.fromISO(value);
        return parsed.isValid ? parsed.toISODate() || '' : '';
    }
    const parsed = DateTime.fromJSDate(value);
    return parsed.isValid ? parsed.toISODate() || '' : '';
};

export const fromIsoDate = (isoDate?: string | null) => {
    if (!isoDate) return undefined;
    const parsed = DateTime.fromISO(isoDate);
    return parsed.isValid ? parsed.toJSDate() : undefined;
};

export const formatIsoDate = (
    isoDate: string,
    regional?: Pick<InvoiceRegionalSettings, 'locale' | 'dateStyle'>,
) => {
    const match = String(isoDate || '').match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (!match) return isoDate || '';
    const date = new Date(Date.UTC(
        Number(match[1]),
        Number(match[2]) - 1,
        Number(match[3]),
    ));
    const locale = (regional?.locale || 'en-US').replace(/_/g, '-');
    const style = String(regional?.dateStyle || 'medium').toLowerCase();
    const dateStyle = style === 'short' || style === 'medium' || style === 'long' || style === 'full'
        ? style
        : 'medium';
    try {
        return new Intl.DateTimeFormat(locale, {
            dateStyle,
            timeZone: 'UTC',
        }).format(date);
    } catch {
        return new Intl.DateTimeFormat('en-US', {
            dateStyle: 'medium',
            timeZone: 'UTC',
        }).format(date);
    }
};

export const formatMinor = (
    amountMinor: number,
    currency = 'USD',
    locale = 'en-US',
) => (
    new Intl.NumberFormat(locale, {
        style: 'currency',
        currency,
    }).format(amountMinor / 100)
);

export const buildInvoiceSmsText = (invoice: Invoice) => {
    const name = invoice.customer.name.trim();
    const amount = formatMinor(
        invoice.document.totals.totalMinor,
        invoice.document.totals.currency,
        invoice.document.design.regional.locale,
    );
    const due = formatIsoDate(invoice.dueOn, invoice.document.design.regional);
    const details = `invoice ${invoice.number} for ${amount} is due ${due}. The PDF is attached.`;
    if (!name) {
        return `Invoice ${invoice.number} for ${amount} is due ${due}. The PDF is attached.`;
    }
    return `Hi ${name}, ${details}`;
};

export const dollarsToMinor = (value: string | number) => {
    const parsed = typeof value === 'number' ? value : Number(value);
    if (!Number.isFinite(parsed)) return 0;
    return Math.round(parsed * 100);
};

export const minorToRateInput = (minor: string) => {
    const parsed = Math.round(Number(minor));
    if (!Number.isFinite(parsed)) return minor;
    const sign = parsed < 0 ? '-' : '';
    const abs = Math.abs(parsed);
    const whole = Math.floor(abs / 100);
    const cents = abs % 100;
    if (cents === 0) return `${sign}${whole}`;
    return `${sign}${whole}.${String(cents).padStart(2, '0')}`;
};

export const feeAmountMinor = (
    fee: InvoiceFee,
    subtotalMinor: number,
) => (
    fee.calculation.type === 'fixed'
        ? fee.calculation.amountMinor
        : Math.round((subtotalMinor * fee.calculation.rateBasisPoints) / 10000)
);

export const lineTotalMinor = (quantity: string, rateMinor: number) => {
    const qty = Number(quantity);
    if (!Number.isFinite(qty) || !Number.isFinite(rateMinor)) return 0;
    return Math.round(qty * rateMinor);
};

export const orderedColumns = (columns: InvoiceBuilderColumn[] = []) => (
    [...columns].sort((left, right) => left.order - right.order)
);

export const visibleColumns = (columns: InvoiceBuilderColumn[] = []) => (
    orderedColumns(columns).filter((column) => column.isVisible)
);

export const customColumns = (columns: InvoiceBuilderColumn[] = []) => (
    columns.filter((column): column is InvoiceBuilderCustomColumn => column.kind === 'custom')
);

export const orderedBillToFields = (fields: InvoiceBillToCustomField[] = []) => (
    [...fields].sort((left, right) => left.order - right.order)
);

export const visibleBillToFields = (fields: InvoiceBillToCustomField[] = []) => (
    orderedBillToFields(fields).filter((field) => field.isVisible)
);

export const fieldDefault = (value?: string | null) => String(value ?? '').trim();

export const lineDraftFromColumns = (columns: InvoiceBuilderColumn[] = []) => {
    const customValues: Record<string, string> = {};
    let description = '';
    let quantity = '1';
    let rate = '0';
    columns.forEach((column) => {
        const fallback = fieldDefault(column.defaultValue);
        if (!fallback) return;
        if (column.kind === 'description') description = fallback;
        else if (column.kind === 'quantity') quantity = fallback;
        else if (column.kind === 'rate') rate = minorToRateInput(fallback);
        else if (column.kind === 'custom') customValues[column.id] = fallback;
    });
    return {
        description,
        quantity,
        rate,
        customValues,
    };
};

export const billToDefaultValues = (fields: InvoiceBillToCustomField[] = []) => {
    const values: Record<string, string | Date> = {};
    orderedBillToFields(fields).forEach((field) => {
        const fallback = fieldDefault(field.defaultValue);
        if (!fallback) return;
        if (field.valueKind === 'date') {
            const date = fromIsoDate(fallback);
            if (date) values[field.id] = date;
            return;
        }
        values[field.id] = fallback;
    });
    return values;
};

export const invoiceNotes = (invoice: Pick<Invoice, 'notes' | 'document'>) => {
    const note = String(invoice.document.note ?? invoice.document.notes ?? invoice.notes ?? '').trim();
    return note;
};

export const getCustomValue = (
    values: InvoiceCustomValue[],
    columnId: string,
) => values.find((value) => value.columnId === columnId)?.value || '';

export const getBillToCustomValue = (
    values: InvoiceBillToCustomValue[],
    fieldId: string,
) => values.find((value) => value.fieldId === fieldId)?.value || '';

export const invoicesPath = (organizationId: string, query: ListInvoicesQuery = {}) => {
    const params = new URLSearchParams();
    if (query.customerId) params.set('customerId', query.customerId);
    if (query.issuedFrom) params.set('issuedFrom', query.issuedFrom);
    if (query.issuedTo) params.set('issuedTo', query.issuedTo);
    if (query.dueFrom) params.set('dueFrom', query.dueFrom);
    if (query.dueTo) params.set('dueTo', query.dueTo);
    if (query.status) params.set('status', query.status);
    if (query.cursor) params.set('cursor', query.cursor);
    if (query.limit) params.set('limit', String(query.limit));
    const search = params.toString();
    return `/organizations/${organizationId}/apps/invoice-builder/invoices${search ? `?${search}` : ''}`;
};

export const invoicePath = (organizationId: string, invoiceId: string) => (
    `/organizations/${organizationId}/apps/invoice-builder/invoices/${invoiceId}`
);

export const invoiceActivitiesPath = (
    organizationId: string,
    invoiceId: string,
    query: { cursor?: string; limit?: number } = {},
) => {
    const params = new URLSearchParams();
    if (query.cursor) params.set('cursor', query.cursor);
    if (query.limit) params.set('limit', String(query.limit));
    const search = params.toString();
    return `${invoicePath(organizationId, invoiceId)}/activities${search ? `?${search}` : ''}`;
};

export const invoiceContentPath = (organizationId: string, invoiceId: string) => (
    `${invoicePath(organizationId, invoiceId)}/content`
);

export const invoiceInternalNotePath = (organizationId: string, invoiceId: string) => (
    `${invoicePath(organizationId, invoiceId)}/internal-note`
);

export const signingSessionPath = (organizationId: string, invoiceId: string) => (
    `/organizations/${organizationId}/apps/invoice-builder/invoices/${invoiceId}/signing-session`
);

export const isSignatureRequested = (
    design?: { signatureRequest?: { enabled?: boolean } } | null,
) => Boolean(design?.signatureRequest?.enabled);

export const invoiceEditRequiresChangeReason = (
    status: InvoiceStatus,
    signatureState?: InvoiceSignatureState | 'none',
) => status !== 'draft' || signatureState === 'signed';

export const canEditInvoice = (
    status: InvoiceStatus,
    signatureState?: InvoiceSignatureState | 'none',
) => {
    if (status === 'void') return true;
    if (signatureState === 'signed') return false;
    if (status === 'draft' || status === 'paid' || status === 'overdue') return true;
    if (status !== 'sent') return false;
    return signatureState === 'none'
        || signatureState === 'awaiting'
        || signatureState === 'unrequested';
};

export const isSigningLinkActive = (expiresAt?: string | null) => {
    if (!expiresAt) return false;
    const time = new Date(expiresAt).getTime();
    return !Number.isNaN(time) && time > Date.now();
};

export const invoiceSignatureState = (
    invoice: Pick<Invoice, 'status' | 'document'>,
    acceptance?: InvoiceAcceptance | null,
    hasActiveLink = true,
): InvoiceSignatureState | 'none' => {
    if (acceptance) return 'signed';
    if (isSignatureRequested(invoice.document.design) && invoice.status !== 'void') {
        return hasActiveLink ? 'awaiting' : 'unrequested';
    }
    return 'none';
};

export const signatureImageUrl = (acceptance?: InvoiceAcceptance | null) => {
    const image = acceptance?.signatureDataUrl?.trim() || '';
    return image.startsWith('data:image/') ? image : '';
};

export const signatureMethodLabel = (acceptance?: InvoiceAcceptance | null) => {
    if (acceptance?.method === 'draw') return 'Drawn signature';
    if (acceptance?.method === 'type') return 'Typed signature';
    return '';
};

export const formatAcceptedAt = (acceptedAt: string, locale: string) => {
    const date = new Date(acceptedAt);
    if (Number.isNaN(date.getTime())) return '';
    const day = new Intl.DateTimeFormat(locale, {
        dateStyle: 'medium',
        timeZone: 'UTC',
    }).format(date);
    const time = new Intl.DateTimeFormat(locale, {
        timeStyle: 'short',
        timeZone: 'UTC',
    }).format(date);
    return `${day} at ${time} UTC`;
};

export const signatureStatusLabel = (
    state: InvoiceSignatureState | 'none',
    acceptance?: InvoiceAcceptance | null,
) => {
    if (state === 'awaiting') return 'Awaiting signature';
    if (state === 'unrequested') return 'Signature not requested';
    if (state !== 'signed') return '';
    const name = acceptance?.name?.trim();
    return name ? `Signed by ${name}` : 'Signed';
};

export const templatesPath = (organizationId: string, cursor?: string, limit = 50) => {
    const params = new URLSearchParams();
    params.set('limit', String(limit));
    if (cursor) params.set('cursor', cursor);
    return `/organizations/${organizationId}/apps/invoice-builder/templates?${params.toString()}`;
};

export const statusLabel = (status: InvoiceStatus) => {
    const labels: Record<InvoiceStatus, string> = {
        draft: 'Draft',
        sent: 'Sent',
        overdue: 'Overdue',
        paid: 'Paid',
        void: 'Void',
    };
    return labels[status];
};

export const storedCustomValue = (
    field: { valueKind: InvoiceBuilderCustomColumn['valueKind'] },
    value: unknown,
) => (
    field.valueKind === 'date'
        ? toIsoDate(value as Date | string)
        : String(value || '')
);

export const billToCustomValuesPayload = (
    fields: InvoiceBillToCustomField[] = [],
    values: Record<string, unknown> = {},
): InvoiceBillToCustomValue[] => {
    const seen = new Set<string>();
    return orderedBillToFields(fields).flatMap((field) => {
        if (seen.has(field.id)) return [];
        seen.add(field.id);
        const value = storedCustomValue(field, values[field.id]).trim();
        return value ? [{ fieldId: field.id, value }] : [];
    });
};

export const formatCustomValue = (value: string, valueKind: InvoiceBuilderCustomColumn['valueKind']) => {
    if (!value) return '';
    if (valueKind === 'boolean') return value === 'true' ? 'Yes' : 'No';
    if (valueKind === 'date') return formatIsoDate(value);
    return value;
};

export const newIdempotencyKey = () => (
    typeof crypto !== 'undefined' && crypto.randomUUID
        ? crypto.randomUUID()
        : `invoice-${Date.now()}-${Math.random().toString(16).slice(2)}`
);

export const invoiceToFormValues = (
    invoice: Invoice,
    options: {
        customer?: Record<string, unknown> | null;
        templateName?: string;
        templateVersionId?: string;
    } = {},
) => {
    const columns = orderedColumns(invoice.document.design.columns || []);
    const customs = customColumns(columns);
    const billToCustomValues: Record<string, string | Date> = {};
    orderedBillToFields(invoice.document.design.billToFields).forEach((field) => {
        const raw = getBillToCustomValue(invoice.document.billToCustomValues, field.id);
        if (!raw) return;
        if (field.valueKind === 'date') {
            const date = fromIsoDate(raw);
            if (date) billToCustomValues[field.id] = date;
            return;
        }
        billToCustomValues[field.id] = raw;
    });
    const lines = (invoice.document.lines || []).map((line) => ({
        key: line.id,
        description: line.description,
        quantity: line.quantity,
        rate: minorToRateInput(String(line.rateMinor)),
        customValues: Object.fromEntries(
            customs.map((column) => [column.id, getCustomValue(line.customValues, column.id)]),
        ),
    }));
    const status: InvoiceCreateStatus = invoice.status === 'sent' || invoice.status === 'paid'
        ? invoice.status
        : 'draft';
    const template: InvoiceBuilderTemplate = {
        id: invoice.templateId,
        name: options.templateName
            || invoice.document.design.labels?.documentTitle
            || 'Template',
        description: '',
        isActive: true,
        usageCount: 0,
        versionId: options.templateVersionId || '',
        design: invoice.document.design,
        createdAt: invoice.createdAt,
        updatedAt: invoice.updatedAt,
    };

    return {
        template,
        customer: options.customer || {
            id: invoice.customer?.id || '',
            displayName: invoice.customer?.name || '',
            phoneNumber: invoice.customer?.phone || '',
            email: invoice.customer?.email || '',
        },
        issuedOn: fromIsoDate(invoice.issuedOn),
        dueOn: fromIsoDate(invoice.dueOn),
        status,
        lines: lines.length > 0 ? lines : [{
            key: newIdempotencyKey(),
            description: '',
            quantity: '1',
            rate: '0',
            customValues: {},
        }],
        billToCustomValues,
        notes: invoiceNotes(invoice),
        internalNote: invoice.internalNote || invoice.document.internalNote || '',
    };
};

export const toInvoiceContentBody = (
    values: {
        template?: InvoiceBuilderTemplate;
        issuedOn?: Date | string | null;
        dueOn?: Date | string | null;
        notes?: unknown;
        lines?: DraftInvoiceLine[];
        billToCustomValues?: Record<string, unknown>;
        changeReason?: string;
    },
    expectedUpdatedAt?: string,
): InvoiceContentBody => {
    const columns = orderedColumns(values.template?.design.columns || []);
    const changeReason = String(values.changeReason || '').trim().slice(
        0,
        INVOICE_CHANGE_REASON_MAX_LENGTH,
    );
    return {
        issuedOn: toIsoDate(values.issuedOn),
        dueOn: toIsoDate(values.dueOn),
        note: String(values.notes || '').trim(),
        lines: (values.lines || []).map((line) => ({
            id: line.key,
            description: line.description,
            quantity: line.quantity,
            rateMinor: dollarsToMinor(line.rate),
            customValues: customColumns(columns).map((column) => ({
                columnId: column.id,
                value: storedCustomValue(column, line.customValues?.[column.id]),
            })),
        })),
        billToCustomValues: billToCustomValuesPayload(
            values.template?.design.billToFields,
            values.billToCustomValues,
        ),
        ...(changeReason ? { changeReason } : {}),
        ...(expectedUpdatedAt ? { expectedUpdatedAt } : {}),
    };
};

export interface DraftInvoiceLine {
    key: string;
    description: string;
    quantity: string;
    rate: string;
    customValues?: Record<string, string>;
}

interface DraftBuyerSource {
    id?: string;
    email?: string;
    phoneNumber?: string;
    firstName?: string;
    lastName?: string;
    alias?: string;
    displayName?: string;
}

export const toInvoiceBuyer = (customer?: DraftBuyerSource | null): InvoiceCustomer => ({
    id: customer?.id || '',
    name: customer ? getDisplayName(customer) : '',
    phone: customer?.phoneNumber || '',
    email: customer?.email || '',
});

export const buildDraftInvoice = ({
    template,
    customer,
    issuedOn,
    dueOn,
    lines = [],
    billToCustomValues = {},
    notes = '',
}: {
    template: InvoiceBuilderTemplate;
    customer?: DraftBuyerSource | null;
    issuedOn?: Date | string | null;
    dueOn?: Date | string | null;
    lines?: DraftInvoiceLine[];
    billToCustomValues?: Record<string, unknown>;
    notes?: string;
}): Invoice => {
    const columns = orderedColumns(template.design.columns || []);
    const currency = template.design.regional.currency || 'USD';
    const buyer = toInvoiceBuyer(customer);
    const documentLines: InvoiceLine[] = lines.map((line) => {
        const rateMinor = dollarsToMinor(line.rate);
        return {
            id: line.key,
            description: line.description,
            quantity: line.quantity,
            rateMinor,
            customValues: customColumns(columns).map((column) => ({
                columnId: column.id,
                value: storedCustomValue(column, line.customValues?.[column.id]),
            })),
            amounts: { totalMinor: lineTotalMinor(line.quantity, rateMinor) },
        };
    });
    const subtotalMinor = documentLines.reduce(
        (sum, line) => sum + line.amounts.totalMinor,
        0,
    );
    const fees = (template.design.fees || []).map((fee) => ({
        ...fee,
        amountMinor: feeAmountMinor(fee, subtotalMinor),
    }));
    const totalMinor = subtotalMinor + fees.reduce((sum, fee) => sum + fee.amountMinor, 0);
    const invoiceNotesValue = String(notes || '');

    return {
        id: '',
        number: '',
        status: 'draft',
        templateId: template.id,
        customer: buyer,
        issuedOn: toIsoDate(issuedOn),
        dueOn: toIsoDate(dueOn),
        voidNote: '',
        createdAt: '',
        updatedAt: '',
        document: {
            design: template.design,
            seller: template.design.seller,
            buyer,
            billToCustomValues: billToCustomValuesPayload(
                template.design.billToFields,
                billToCustomValues,
            ),
            lines: documentLines,
            totals: {
                currency,
                subtotalMinor,
                totalMinor,
                fees,
            },
            note: invoiceNotesValue,
        },
    };
};
